import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { money, currentPeriod, periodLabel, shortDate } from "@/lib/format";
import { toast } from "sonner";
import { Download, Mail, Plus, XOctagon, ExternalLink, Pencil, Sparkles, GitMerge } from "lucide-react";
import OverrideEventModal from "@/components/OverrideEventModal";
import OutstandingPayoutsBanner from "@/components/OutstandingPayoutsBanner";
import { SortableTh, makeCycleSort } from "@/components/SortableTh";

// Sortable-column config for the commission events table. Accessors
// match visual columns exactly — money as number, dates as timestamps,
// text lowercased.
const EVENT_SORT_COLUMNS = {
  paid_at:        { accessor: (r) => new Date(r.paid_at || r.created_at || 0).getTime() },
  partner:        { accessor: (r) => (r.partner_code || "").toLowerCase() },
  customer:       { accessor: (r) => (r.customer_name || "").toLowerCase() },
  invoice:        { accessor: (r) => Number(r.invoice_number) || 0 },
  product:        { accessor: (r) => (r.product_name || r.name || "").toLowerCase() },
  source:         { accessor: (r) => (r.source || "").toLowerCase() },
  amount_ex_gst:  { accessor: (r) => Number(r.amount_ex_gst) || 0 },
  rate_pct:       { accessor: (r) => Number(r.rate_pct) || 0 },
  commission:     { accessor: (r) => Number(r.commission_ex_gst) || 0 },
  status:         { accessor: (r) => (r.status || "").toLowerCase() },
};

export default function AdminLedger() {
  // Support ?period=YYYY-MM in the URL so the OutstandingPayoutsBanner can
  // deep-link straight to the month that needs paying. Falls back to the
  // current calendar month on first mount.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPeriod = searchParams.get("period");
  const [period, _setPeriod] = useState(urlPeriod || currentPeriod());
  // Keep the local `period` state in sync with the URL — critical when the
  // OutstandingPayoutsBanner's `?period=` deep-link is clicked while the
  // admin is ALREADY on the ledger (the initial useState value has long
  // since been resolved, so without this effect the URL updates but the
  // ledger stays on the current month).
  useEffect(() => {
    const next = urlPeriod || currentPeriod();
    _setPeriod((prev) => (prev === next ? prev : next));
  }, [urlPeriod]);
  const setPeriod = (next) => {
    _setPeriod(next);
    // Keep the URL in sync so a refresh (or a shared link) stays on the same month.
    const q = new URLSearchParams(searchParams);
    if (next === currentPeriod()) q.delete("period"); else q.set("period", next);
    setSearchParams(q, { replace: true });
  };
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManual, setShowManual] = useState(false);
  const [overrideEvent, setOverrideEvent] = useState(null);
  const [partners, setPartners] = useState([]);
  const [syncroSubdomain, setSyncroSubdomain] = useState("");
  // Sort for the events table. Default: newest paid first.
  const [eventsSort, setEventsSort] = useState({ key: "paid_at", dir: "desc" });
  const cycleEventsSort = makeCycleSort(setEventsSort, "paid_at", "desc");
  const sortedEvents = useMemo(() => {
    const col = EVENT_SORT_COLUMNS[eventsSort.key];
    if (!col) return events;
    const dir = eventsSort.dir === "asc" ? 1 : -1;
    return [...events].sort((a, b) => {
      const av = col.accessor(a);
      const bv = col.accessor(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
  }, [events, eventsSort]);

  async function reload() {
    setLoading(true);
    try {
      const [s, ev, p, cfg] = await Promise.all([
        api.get(`/commissions/summary/${period}`),
        api.get(`/commissions/events`, { params: { period } }),
        api.get(`/commissions/partners`),
        api.get(`/commissions/config`),
      ]);
      setSummary(s.data);
      setEvents(ev.data);
      setPartners(p.data);
      setSyncroSubdomain(cfg.data.syncro_subdomain || "");
    } catch (_) {
      toast.error("Failed to load ledger");
    } finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, [period]);

  async function createPayout(partner_code) {
    try {
      await api.post("/commissions/payouts", { partner_code, period });
      toast.success("Payout draft created");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  }

  async function markPaid(payout_id, siblings = []) {
    // Sibling pending/on-hold payouts for the same partner+period. If any
    // exist, offer to merge them all into one final statement so the partner
    // receives a single PDF instead of several.
    const otherPending = siblings.filter(
      (po) => po.id !== payout_id && po.status !== "paid"
    );
    let targetId = payout_id;
    if (otherPending.length > 0) {
      const totalSibs = otherPending.length + 1;
      const doMerge = window.confirm(
        `This partner has ${totalSibs} pending payouts for the same period.\n\n` +
        `Click OK to MERGE them all into one final statement, then mark as paid ` +
        `(recommended — one PDF for the partner).\n\n` +
        `Click Cancel to mark only THIS payout as paid and leave the others pending.`
      );
      if (doMerge) {
        try {
          const ids = [payout_id, ...otherPending.map((po) => po.id)];
          const { data } = await api.post("/commissions/payouts/merge", { payout_ids: ids });
          targetId = data.merged.id;
          toast.success(
            `Merged ${totalSibs} payouts into #${targetId.slice(0, 8)} · $${data.amount_ex_gst.toFixed(2)}`
          );
        } catch (e) {
          toast.error(
            formatApiErrorDetail(e.response?.data?.detail) || "Merge failed — no status changed"
          );
          return;
        }
      }
    }
    const ref = window.prompt("Payment reference (bank ref, EFT number)?", "");
    if (ref === null) { reload(); return; }
    try {
      await api.patch(`/commissions/payouts/${targetId}`, {
        status: "paid", payment_reference: ref,
      });
      toast.success(
        otherPending.length > 0 && targetId !== payout_id
          ? "Merged & marked paid"
          : "Marked paid"
      );
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  }

  async function unpay(payout_id, status) {
    // Paid → reopen (non-destructive: keep the payout row, revert to pending).
    // Pending/on-hold → delete (destructive: remove the payout, release events
    // so they can be re-drafted).
    const isPaid = status === "paid";
    const msg = isPaid
      ? "Reopen this paid payout? It will flip back to Pending, the payment reference will be cleared, and you can adjust and re-issue. The event history is preserved."
      : "Delete this pending payout? Its events will be released back to unpaid so you can re-draft.";
    if (!window.confirm(msg)) return;
    try {
      if (isPaid) {
        await api.post(`/commissions/payouts/${payout_id}/reopen`);
        toast.success("Payout reopened — status set to Pending");
      } else {
        await api.delete(`/commissions/payouts/${payout_id}`);
        toast.success("Payout deleted — events released");
      }
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  }

  async function mergePayouts(payoutIds, partnerName) {
    if (!payoutIds || payoutIds.length < 2) return;
    if (!window.confirm(
      `Merge ${payoutIds.length} payouts for ${partnerName} into one? ` +
      "Events will roll into the oldest pending payout, the rest will be deleted. " +
      "Paid payouts must be reopened first."
    )) return;
    try {
      const { data } = await api.post("/commissions/payouts/merge", { payout_ids: payoutIds });
      toast.success(
        `Merged into #${data.merged.id.slice(0, 8)} — ${data.event_count} events · $${data.amount_ex_gst.toFixed(2)}`
      );
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Merge failed");
    }
  }

  async function sendEmail(payout_id) {
    try {
      const r = await api.post(`/commissions/payouts/${payout_id}/send-email`);
      toast.success(`Report emailed to ${r.data.to}`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Email failed");
    }
  }

  async function voidEvent(id) {
    const reason = window.prompt("Reason for voiding this event?", "");
    if (reason === null) return;
    try {
      await api.post(`/commissions/events/${id}/void`, { reason });
      toast.success("Event voided");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  }

  return (
    <div className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-cyan-500 font-semibold mb-3">Ledger</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight font-medium">Commission ledger</h1>
          <p className="text-slate-400 mt-2">{periodLabel(period)} · per-partner roll-up and event drill-down</p>
        </div>
        <div className="flex gap-3">
          <input
            data-testid="ledger-period"
            type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
            className="rounded-lg border border-slate-800 bg-slate-900 px-4 py-2.5 font-mono text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
          />
          <button
            data-testid="add-manual-event"
            onClick={() => setShowManual(true)}
            className="inline-flex items-center gap-2 border border-slate-800 bg-slate-900 hover:bg-slate-950 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" /> Manual event
          </button>
        </div>
      </header>

      {/* Multi-row breakdown of every prior month with anything
          outstanding — matches the Overview banner so admins get the
          same "which months aren't finalised yet" view once they
          drill into Financial. Current month is deliberately excluded
          server-side (period < current) because charges are still
          accumulating until month-end. */}
      <OutstandingPayoutsBanner variant="compact" testid="ledger-outstanding-payouts" />
      <p
        data-testid="ledger-current-period-note"
        className="text-[11px] text-slate-500 italic"
      >
        The current month isn&rsquo;t shown here &mdash; charges can still accumulate
        until the last day, so it&rsquo;s only due for finalisation on the 1st
        of the following month.
      </p>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : summary && (
        <>
          {/* Totals */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <TotalTile label="Total ex-GST" value={money(summary.total_ex_gst)} testid="tot-ex" />
            <TotalTile label="Total GST" value={money(summary.total_gst)} testid="tot-gst" />
            <TotalTile label="Grand total" value={money(summary.grand_total)} accent testid="tot-grand" />
          </div>

          {/* Roll-up per partner */}
          <div className="rounded-xl border border-slate-800/80 bg-slate-900 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800">
              <h3 className="font-display text-xl">Per-partner rollup · {periodLabel(period)}</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <tr>
                  <th className="text-left px-6 py-3">Partner</th>
                  <th className="text-right px-6 py-3">Events</th>
                  <th className="text-right px-6 py-3">Ex-GST</th>
                  <th className="text-right px-6 py-3">GST</th>
                  <th className="text-right px-6 py-3">Payable</th>
                  <th className="text-left px-6 py-3">Payout status</th>
                  <th className="text-right px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100" data-testid="rollup-tbody">
                {summary.partners.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-10 text-slate-500">No commission activity in this period.</td></tr>
                )}
                {summary.partners.map((p) => {
                  const payouts = p.payouts || (p.payout ? [p.payout] : []);
                  const coveredEvents = payouts.reduce((s, po) => s + (po.event_count || 0), 0);
                  const coveredAmount = payouts.reduce((s, po) => s + (po.amount_ex_gst || 0), 0);
                  const uncovered = Math.max(0, p.event_count - coveredEvents);
                  return (
                  <tr key={p.partner_code} className="hover:bg-slate-950/50 align-top">
                    <td className="px-6 py-4">
                      <div className="font-medium">{p.partner_name}</div>
                      <div className="text-xs text-slate-500 font-mono">{p.partner_code}</div>
                    </td>
                    <td className="px-6 py-4 text-right font-mono">
                      {p.event_count}
                      {uncovered > 0 && (
                        <div
                          data-testid={`uncovered-${p.partner_code}`}
                          className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold mt-1"
                          title={`${uncovered} event${uncovered === 1 ? "" : "s"} not attached to any payout yet`}
                        >
                          {uncovered} orphan
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right font-mono">{money(p.commission_ex_gst)}</td>
                    <td className="px-6 py-4 text-right font-mono text-slate-400">{money(p.gst)}</td>
                    <td className="px-6 py-4 text-right font-mono font-medium text-cyan-400">{money(p.total_payable)}</td>
                    <td className="px-6 py-4">
                      {payouts.length === 0 ? (
                        <span className="text-xs text-slate-500">no payout</span>
                      ) : (
                        <div className="space-y-2 min-w-[280px]">
                          {payouts.map((po, idx) => (
                            <div
                              key={po.id}
                              data-testid={`payout-line-${p.partner_code}-${idx}`}
                              className="flex items-center justify-between gap-3 rounded-md border border-slate-800 bg-slate-950/40 px-2.5 py-2"
                            >
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <StatusBadge status={po.status} />
                                  {payouts.length > 1 && idx > 0 && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[10px] font-semibold uppercase tracking-wider">
                                      SUPP
                                    </span>
                                  )}
                                </div>
                                <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                                  #{po.id.slice(0, 8)} · {po.event_count || 0} evt · {money(po.amount_ex_gst || 0)}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <a
                                  data-testid={`pdf-${p.partner_code}-${idx}`}
                                  href={`${API_BASE}/commissions/payouts/${po.id}/report.pdf`}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Download PDF"
                                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-800 hover:bg-slate-800 text-slate-300"
                                >
                                  <Download className="w-3 h-3" />
                                </a>
                                <button
                                  data-testid={`email-${p.partner_code}-${idx}`}
                                  onClick={() => sendEmail(po.id)}
                                  title="Send by email"
                                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-800 hover:bg-slate-800 text-slate-300"
                                >
                                  <Mail className="w-3 h-3" />
                                </button>
                                {po.status !== "paid" ? (
                                  <button
                                    data-testid={`mark-paid-${p.partner_code}-${idx}`}
                                    onClick={() => markPaid(po.id, payouts)}
                                    className="text-[11px] font-semibold text-cyan-400 hover:text-cyan-300 px-1.5"
                                  >
                                    Mark paid
                                  </button>
                                ) : (
                                  <button
                                    data-testid={`unpay-${p.partner_code}-${idx}`}
                                    onClick={() => unpay(po.id, po.status)}
                                    title="Reopen this paid payout back to Pending"
                                    className="text-[11px] font-semibold text-rose-500 hover:text-rose-400 px-1.5"
                                  >
                                    Reopen
                                  </button>
                                )}
                                {po.status !== "paid" && (
                                  <button
                                    data-testid={`delete-${p.partner_code}-${idx}`}
                                    onClick={() => unpay(po.id, po.status)}
                                    title="Delete this pending payout — releases events"
                                    className="text-[11px] font-semibold text-rose-500 hover:text-rose-400 px-1.5"
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                          <div className="text-[11px] text-slate-500 font-mono">
                            Covered {coveredEvents}/{p.event_count} events · {money(coveredAmount)}
                            {" of "}
                            {money(p.commission_ex_gst)}
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      {(() => {
                        const nonPaid = payouts.filter((po) => po.status !== "paid");
                        return nonPaid.length >= 2 && (
                          <button
                            data-testid={`merge-${p.partner_code}`}
                            onClick={() => mergePayouts(nonPaid.map((po) => po.id), p.partner_name)}
                            title={`Merge ${nonPaid.length} pending payouts into one`}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 font-semibold"
                          >
                            <GitMerge className="w-3 h-3" /> Merge {nonPaid.length}
                          </button>
                        );
                      })()}
                      {uncovered > 0 && (
                        <button
                          data-testid={`create-supp-${p.partner_code}`}
                          onClick={() => createPayout(p.partner_code)}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
                        >
                          <Plus className="w-3 h-3" /> {payouts.length ? "Add supp." : "Create payout"}
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Events */}
          <div className="rounded-xl border border-slate-800/80 bg-slate-900 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800">
              <h3 className="font-display text-xl">Events · {events.length}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  <tr>
                    <SortableTh sortKey="paid_at"       sort={eventsSort} onSort={cycleEventsSort} label="Paid"      testidPrefix="events-sort" />
                    <SortableTh sortKey="partner"       sort={eventsSort} onSort={cycleEventsSort} label="Partner"   testidPrefix="events-sort" />
                    <SortableTh sortKey="customer"      sort={eventsSort} onSort={cycleEventsSort} label="Customer"  testidPrefix="events-sort" />
                    <SortableTh sortKey="invoice"       sort={eventsSort} onSort={cycleEventsSort} label="Invoice"   testidPrefix="events-sort" />
                    <SortableTh sortKey="product"       sort={eventsSort} onSort={cycleEventsSort} label="Product"   testidPrefix="events-sort" />
                    <SortableTh sortKey="source"        sort={eventsSort} onSort={cycleEventsSort} label="Source"    testidPrefix="events-sort" />
                    <SortableTh sortKey="amount_ex_gst" sort={eventsSort} onSort={cycleEventsSort} label="Ex-GST"    testidPrefix="events-sort" className="text-right" />
                    <SortableTh sortKey="rate_pct"      sort={eventsSort} onSort={cycleEventsSort} label="Rate"      testidPrefix="events-sort" className="text-right" />
                    <SortableTh sortKey="commission"    sort={eventsSort} onSort={cycleEventsSort} label="Commission" testidPrefix="events-sort" className="text-right" />
                    <SortableTh sortKey="status"        sort={eventsSort} onSort={cycleEventsSort} label="Status"    testidPrefix="events-sort" />
                    <th className="text-right px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100" data-testid="events-tbody">
                  {events.length === 0 && (
                    <tr><td colSpan={11} className="text-center py-10 text-slate-500">No events. Test the webhook or add a manual event.</td></tr>
                  )}
                  {sortedEvents.map((e) => {
                    const invUrl = e.invoice_url
                      || (e.invoice_id && syncroSubdomain
                            ? `https://${syncroSubdomain}.syncromsp.com/invoices/${e.invoice_id}`
                            : null);
                    const labels = Array.from(new Set(
                      (e.line_breakdown?.lines || [])
                        .map((l) => l.matched_label)
                        .filter(Boolean)
                    ));
                    return (
                    <tr key={e.id} className={`hover:bg-slate-950/50 ${e.status === "voided" ? "opacity-50 line-through" : ""}`}>
                      <td className="px-6 py-3 font-mono text-xs">{shortDate(e.paid_at)}</td>
                      <td className="px-6 py-3">{e.partner_name} <span className="text-xs text-slate-500 font-mono">· {e.partner_code}</span></td>
                      <td className="px-6 py-3 text-slate-300">{e.customer_name || (e.customer_id ? `#${e.customer_id}` : "—")}</td>
                      <td className="px-6 py-3">
                        {e.invoice_id ? (
                          invUrl ? (
                            <a
                              data-testid={`invoice-link-${e.id}`}
                              href={invUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 font-mono text-xs text-cyan-400 hover:text-cyan-300 hover:underline"
                              title={`Open invoice #${e.invoice_id} in Syncro`}
                            >
                              #{e.invoice_id} <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <span className="font-mono text-xs text-slate-400">#{e.invoice_id}</span>
                          )
                        ) : (
                          <span className="text-slate-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        {labels.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {labels.map((lbl) => (
                              <span
                                key={lbl}
                                className="inline-flex items-center px-2 py-0.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 text-xs font-medium"
                              >
                                {lbl}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-600 text-xs">{e.plan_name || "—"}</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-xs text-slate-500 font-mono">{e.source || "syncro"}</td>
                      <td className="px-6 py-3 text-right font-mono">{money(e.amount_paid_ex_gst)}</td>
                      <td className="px-6 py-3 text-right font-mono text-slate-400">{e.rate_snapshot}%</td>
                      <td className="px-6 py-3 text-right font-mono font-medium text-cyan-400">
                        <div className="flex items-center justify-end gap-1.5">
                          {e.original_commission_amount != null && (
                            <span
                              data-testid={`override-badge-${e.id}`}
                              title={`Original: ${money(e.original_commission_amount)}${e.override_reason ? " — " + e.override_reason : ""}`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[10px] font-semibold uppercase tracking-wider"
                            >
                              <Sparkles className="w-3 h-3" /> ADJ
                            </span>
                          )}
                          {money(e.commission_amount)}
                        </div>
                      </td>
                      <td className="px-6 py-3"><StatusBadge status={e.status} /></td>
                      <td className="px-6 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          {e.status !== "voided" && (
                            <button
                              data-testid={`override-${e.id}`}
                              onClick={() => setOverrideEvent(e)}
                              title="Override commission amount"
                              className="text-cyan-400 hover:text-cyan-300"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          {e.status !== "voided" && !e.payout_id && (
                            <button data-testid={`void-${e.id}`} onClick={() => voidEvent(e.id)}
                              title="Void this event"
                              className="text-rose-600 hover:text-rose-700"><XOctagon className="w-4 h-4" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showManual && (
        <ManualEventModal partners={partners} period={period} onClose={() => setShowManual(false)} onDone={() => { setShowManual(false); reload(); }} />
      )}

      {overrideEvent && (
        <OverrideEventModal
          event={overrideEvent}
          onClose={() => setOverrideEvent(null)}
          onSaved={() => { setOverrideEvent(null); reload(); }}
        />
      )}
    </div>
  );
}

function TotalTile({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-800/80 bg-slate-900 p-6">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`font-mono text-3xl font-medium mt-2 ${accent ? "text-cyan-500" : "text-slate-50"}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    accrued: "bg-cyan-950 text-cyan-400 border-cyan-900",
    paid: "bg-cyan-500 text-white border-cyan-500",
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    on_hold: "bg-slate-800 text-slate-300 border-slate-800",
    voided: "bg-slate-800 text-slate-500 border-slate-800",
    clawback: "bg-rose-50 text-rose-700 border-rose-100",
  };
  const cls = map[status] || "bg-slate-800 text-slate-300 border-slate-800";
  return <span className={`inline-block text-xs px-2 py-0.5 rounded-full border ${cls} font-medium`}>{status}</span>;
}

function ManualEventModal({ partners, period, onClose, onDone }) {
  const active = useMemo(() => partners.filter((p) => p.active), [partners]);
  const [form, setForm] = useState({
    partner_code: active[0]?.code || "",
    customer_name: "", plan_name: "",
    amount_paid_ex_gst: 0,
    invoice_id: "",
    admin_note: "",
  });
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/commissions/events/manual", {
        ...form,
        amount_paid_ex_gst: Number(form.amount_paid_ex_gst),
        paid_at: `${period}-15T12:00:00Z`, // middle of the period so it lands in this month
      });
      toast.success("Event added");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  }
  return (
    <div data-testid="manual-event-modal" className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-800">
          <h3 className="font-display text-xl">Manual commission event</h3>
          <div className="text-xs text-slate-500 mt-1">Adds a commissionable event for {periodLabel(period)}.</div>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Partner</span>
            <select
              data-testid="manual-partner"
              required value={form.partner_code} onChange={(e) => setForm(f => ({ ...f, partner_code: e.target.value }))}
              className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
            >
              {active.map((p) => <option key={p.code} value={p.code}>{p.name} ({p.code})</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Customer name</span>
              <input data-testid="manual-customer" value={form.customer_name} onChange={(e) => setForm(f => ({ ...f, customer_name: e.target.value }))}
                className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Plan / description</span>
              <input data-testid="manual-plan" value={form.plan_name} onChange={(e) => setForm(f => ({ ...f, plan_name: e.target.value }))}
                className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Amount paid (ex-GST)</span>
            <input data-testid="manual-amount" required type="number" step="0.01" min="0" value={form.amount_paid_ex_gst} onChange={(e) => setForm(f => ({ ...f, amount_paid_ex_gst: e.target.value }))}
              className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 font-mono" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Admin note (optional)</span>
            <input data-testid="manual-note" value={form.admin_note} onChange={(e) => setForm(f => ({ ...f, admin_note: e.target.value }))}
              className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 text-sm" />
          </label>
          <div className="flex justify-end gap-3 pt-2 border-t border-slate-800">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-800 text-sm hover:bg-slate-950">Cancel</button>
            <button data-testid="manual-submit" type="submit" disabled={busy} className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-medium">
              {busy ? "Saving…" : "Add event"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
